import { createStore, createEffect, createEvent, combine } from 'effector';
import { $notificationStore } from '../notificationStore';
import { ApiUtilsV2 } from '@shared/utils';
import {
  CardsApi,
  CardStatus,
  RequestStrategyEnum,
  type CardDetailsResponse,
  type RemindPinRequest,
  type ReportLostStolenRequest,
  type UpdateCategoryCardRequest,
  type UpdateLabelCardRequest,
  type WithSignal,
} from '@shared/api';

const getCardEffect = createEffect(({ signal, ...request }: WithSignal<{ id: number }>) => CardsApi.getCard(request.id, signal));
const activateEffect = createEffect((cardId: number) => CardsApi.activate(cardId));
const updateLabelCardEffect = createEffect((request: UpdateLabelCardRequest) => CardsApi.updateLabelCard(request));
const updateCategoryCardEffect = createEffect((request: UpdateCategoryCardRequest) => CardsApi.updateCategoryCard(request));
const remindPinEffect = createEffect((request: RemindPinRequest) => CardsApi.remindPin(request));
const freezeCardEffect = createEffect((cardId: number) => CardsApi.freezeCard(cardId));
const unfreezeCardEffect = createEffect((cardId: number) => CardsApi.unfreezeCard(cardId));
const reportLostStolenEffect = createEffect((request: ReportLostStolenRequest) => CardsApi.reportLostStolen(request));

const clearEvent = createEvent();

const $dataStore = createStore<CardDetailsResponse | null>(null)
  .on(getCardEffect.done, (current, data) => data.result.data || current)
  .on(freezeCardEffect.done, (current, data) => (data.result.data ? { ...current!, status: data.result.data.status } : current))
  .on(unfreezeCardEffect.done, (current, data) => (data.result.data ? { ...current!, status: data.result.data.status } : current))
  .on(updateLabelCardEffect.done, (current, data) => data.result.data || current)
  .on(updateCategoryCardEffect.done, (current, data) => data.result.data || current)
  .on(activateEffect.done, (current, data) => (data.result.data ? { ...current!, status: CardStatus.ACTIVE } : current))
  .reset(clearEvent);

const $loadingStore = createStore<boolean>(false)
  .on(getCardEffect.pending, (_, data) => data)
  .on(getCardEffect.fail, (_, data) => ApiUtilsV2.abortApiLoadingFailCaseTrigger(data.error))
  .on(getCardEffect.done, () => false)
  .reset(clearEvent);
const $actionLoadingStore = createStore<boolean>(false)
  .on(activateEffect.pending, (_, data) => data)
  .on(activateEffect.fail, () => false)
  .on(activateEffect.done, () => false)
  .on(updateLabelCardEffect.pending, (_, data) => data)
  .on(updateLabelCardEffect.fail, () => false)
  .on(updateLabelCardEffect.done, () => false)
  .on(updateCategoryCardEffect.pending, (_, data) => data)
  .on(updateCategoryCardEffect.fail, () => false)
  .on(updateCategoryCardEffect.done, () => false)
  .on(remindPinEffect.pending, (_, data) => data)
  .on(remindPinEffect.fail, () => false)
  .on(remindPinEffect.done, () => false)
  .on(freezeCardEffect.pending, (_, data) => data)
  .on(freezeCardEffect.fail, () => false)
  .on(freezeCardEffect.done, () => false)
  .on(unfreezeCardEffect.pending, (_, data) => data)
  .on(unfreezeCardEffect.fail, () => false)
  .on(unfreezeCardEffect.done, () => false)
  .on(reportLostStolenEffect.pending, (_, data) => data)
  .on(reportLostStolenEffect.fail, () => false)
  .on(reportLostStolenEffect.done, () => false)
  .reset(clearEvent);

const $errorStore = createStore<string | null>(null)
  .on(getCardEffect, () => null)
  // .on(getCardEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(getCardEffect.fail, (_, data) => {
    if (data.error.message === 'Action is forbidden.') return 'Action is forbidden.';
    if (data.error.message === "'Card Id' must be greater than '0'.") return 'Action is forbidden.';
    return 'Something went wrong while fetching Card. Please try again.';
  })
  .on(getCardEffect.done, () => null)
  .reset(clearEvent);

const $actionErrorStore = createStore<string | null>(null)
  .on(freezeCardEffect, () => null)
  // .on(freezeCardEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(freezeCardEffect.fail, () => 'Something went wrong while freezing Card. Please try again.')
  .on(freezeCardEffect.done, () => null)
  .on(unfreezeCardEffect, () => null)
  // .on(unfreezeCardEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(unfreezeCardEffect.fail, () => 'Something went wrong while unfreezing Card. Please try again.')
  .on(unfreezeCardEffect.done, () => null)
  .on(remindPinEffect, () => null)
  // .on(remindPinEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(remindPinEffect.fail, (_, data) => {
    if (data.error.message === 'Password not correct') return 'Password not correct';
    return 'Something went wrong while requesting PIN. Please try again.';
  })
  .on(remindPinEffect.done, () => null)
  .on(updateLabelCardEffect, () => null)
  // .on(updateLabelCardEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(updateLabelCardEffect.fail, () => 'Something went wrong while updating Card Label. Please try again.')
  .on(updateLabelCardEffect.done, () => null)
  .on(updateCategoryCardEffect, () => null)
  // .on(updateCategoryCardEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(updateCategoryCardEffect.fail, () => 'Something went wrong while updating Card Category. Please try again.')
  .on(updateCategoryCardEffect.done, () => null)
  .on(reportLostStolenEffect, () => null)
  // .on(reportLostStolenEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(reportLostStolenEffect.fail, () => 'Something went wrong while reporting Card. Please try again.')
  .on(reportLostStolenEffect.done, () => null)
  .on(activateEffect, () => null)
  // .on(activateEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(activateEffect.fail, () => 'Something went wrong while activating Card. Please try again.')
  .on(activateEffect.done, () => null)
  .reset(clearEvent);
$actionErrorStore.watch((error) => $notificationStore.events.setError(error));

const getCardApi = ApiUtilsV2.wrapperApi(getCardEffect, RequestStrategyEnum.Abort);
clearEvent.watch(() => getCardApi.cancel());

export const $cardStore = {
  events: {
    getCard: getCardApi.trigger,
    freezeCard: freezeCardEffect,
    unfreezeCard: unfreezeCardEffect,
    remindPin: remindPinEffect,
    updateLabelCard: updateLabelCardEffect,
    updateCategoryCard: updateCategoryCardEffect,
    reportLostStolen: reportLostStolenEffect,
    activate: activateEffect,
    clear: clearEvent,
  },
  store: combine({
    loading: $loadingStore,
    actionLoading: $actionLoadingStore,
    data: $dataStore,
    error: $errorStore,
  }),
};
